import { z } from 'zod';
import { apiGet } from '@/lib/api';

const TotalsSchema = z.object({
  machines: z.number(),
  /** Automaty, których AGENT odezwał się w oknie progu — to samo, co zielone na liście automatów. */
  machinesOnline: z.number(),
  /** Automaty bez agenta. Zadanie wdrożeniowe, nie awaria — dlatego liczone osobno od milczących. */
  machinesNoAgent: z.number().optional(),
  /**
   * ZDROWIE AUTOMATÓW — liczby i gotowa ocena z serwera.
   *
   * ⚠️ `optional`, bo instancja starsza od 01.09.2026 tych pól nie oddaje: kafel ma wtedy wyglądać
   * jak dotąd, a nie wywalić walidację całego pulpitu.
   */
  machinesSpsuiDown: z.number().optional(),
  machinesMaintUp: z.number().optional(),
  machinesDbDown: z.number().optional(),
  machinesSilent: z.number().optional(),
  zdrowieAutomatow: z
    .object({ ton: z.enum(['destructive', 'warning']).nullable(), powody: z.array(z.string()) })
    .optional(),
  items: z.number(),
  employees: z.number(),
  transactions: z.number(),
  transactions7d: z.number(),
  alertsOpen: z.number(),
  alertsCritical: z.number(),
  changesQueued: z.number(),
  changesConfirmed: z.number(),
  lowStock: z.number(),
});

const OverviewSchema = z.object({
  /** Kafle dotyczą TEJ wyspy — zakres jest częścią odpowiedzi, nie domysłem klienta. */
  island: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    client: z.string(),
    cdcMode: z.enum(['TRIGGER_JOURNAL', 'NATIVE_CT']),
    /**
     * STAN SILNIKA SYNCHRONIZACJI TEJ WYSPY — liczony z węzłów, nie z flagi.
     *
     * ⚠️ POWSTAŁO Z MARTWEJ FLAGI: pulpit czytał `features.sync` z `GET /api/config`, gdzie stało
     * zaszyte `false` z czasów v1 — i pisał „wyłączony", gdy trzy maszyny i chmurowy master
     * synchronizowały bez przerwy. `nullable`/`optional`, bo instancja starsza od tej zmiany pola
     * nie odda, a pulpit ma wtedy pokazać wiersz jako nieustalony, nie wywalić walidację.
     */
    synchronizacja: z
      .object({
        czynna: z.boolean(),
        wezlowZBaza: z.number(),
        masterBaza: z.string().nullable(),
        masterWezel: z.string().nullable(),
        masterOk: z.boolean().nullable(),
        masterOdKontaktuMin: z.number().nullable(),
        masterProblem: z.string().nullable(),
        /**
         * GDZIE STOI MASTER — `MASZYNA` na zwykłej wyspie, `WEZEL`/`NIEZASIANY` w Smart Syncu.
         *
         * ⚠️ POLE OPCJONALNE CELOWO: przy wdrożeniu klient bywa nowszy od API na kilka minut,
         * a pole wymagane wywaliłoby walidację CAŁEGO przeglądu — czyli pulpit zamiast zdania.
         */
        gdzieMaster: z.enum(['MASZYNA', 'WEZEL', 'NIEZASIANY']).optional(),
        masterPotwierdzony: z.boolean().optional(),
        /** Tryb wyspy — pulpit pisze go w kafelku obok „włączony". Opcjonalny z tego samego powodu. */
        tryb: z.enum(['ZWYKLY', 'SMART_SYNC']).optional(),
      })
      .nullable()
      .optional(),
    totals: TotalsSchema,
  }),
  /** Zakres: cała instancja. Pokazywane osobno, żeby nie mieszać z kaflami wyspy. */
  fleet: z.object({ clients: z.number(), islands: z.number() }),
  groups: z.array(
    z.object({
      id: z.string(),
      client: z.string(),
      name: z.string(),
      cdcMode: z.enum(['TRIGGER_JOURNAL', 'NATIVE_CT']),
      machines: z.number(),
      employees: z.number(),
      transactions: z.number(),
      /**
       * STAN WYSPY — po to, żeby nie trzeba było przeskakiwać przełącznikiem przez wszystkie, żeby
       * sprawdzić, czy na którejś nie stoi agent. `nullable`, bo instancja starsza od tej zmiany
       * tego pola nie odda, a pulpit ma wtedy pokazać tabelę bez kolumny stanu, nie błąd walidacji.
       */
      zdrowie: z
        .object({
          stan: z.enum(['OK', 'UWAGA', 'AWARIA']),
          powody: z.array(z.string()),
          agenci: z.object({
            potwierdzone: z.number(),
            odpowiada: z.number(),
            milczy: z.number(),
            nigdyNieOdpowiedzial: z.number(),
            czeka: z.number(),
            odrzucone: z.number(),
            minutOdKontaktu: z.number().nullable(),
          }),
          /**
           * TABELE W BAZIE KLIENTA BEZ KLUCZA GŁÓWNEGO.
           *
           * ⚠️⚠️ STRUKTURA OBOK POWODU, dokładnie z tego samego powodu co `nieudaneOperacje`
           * wyżej: z napisu nie da się zrobić odnośnika ani polecenia do skopiowania. Portal
           * wiedział o tym od stycznia (agent melduje w każdym cyklu), ale wiedza siedziała
           * w prozie i nikt z niej nic nie zrobił.
           *
           * `optional`, bo starsza instancja tego pola nie odda — wtedy zostaje sam powód.
           */
          bezKlucza: z
            .object({ tabele: z.array(z.string()), skutki: z.array(z.string()) })
            .nullable()
            .optional(),
        })
        .nullable()
        .optional(),
      /**
       * ILE NIEUDANYCH OPERACJI CZEKA NA SKWITOWANIE.
       *
       * ⚠️⚠️ LICZBA OSOBNO OD TEKSTU POWODU, i to jest cała różnica między „wiem, że coś jest"
       * a „wiem, gdzie kliknąć". `powody` są napisami dla człowieka — z napisu nie da się zrobić
       * odnośnika bez zgadywania jego treści. Ta liczba pozwala pulpitowi wystawić link WPROST
       * do zmian tej wyspy z filtrem „nie udało się", czego brak był sednem zgłoszenia.
       *
       * `optional`, bo starsza instancja tego pola nie odda — wtedy pulpit pokazuje sam powód.
       */
      nieudaneOperacje: z.number().optional(),
    }),
  ),
});

export type Overview = z.infer<typeof OverviewSchema>;

export const fetchOverview = (groupId: string) => apiGet(`/overview?groupId=${groupId}`, OverviewSchema);
